import React, { useEffect, useState } from 'react';
import {
    Panel,
    TextField,
    IPanelStyles,
    PanelType,
    Checkbox,
    PrimaryButton,
    mergeStyleSets,
} from '@fluentui/react';
import LoaderComponent from '../loader/loader.component';
import ErrorMessageComponent from '../error-message/error-message.component';
import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemTarea from '../../models/ItemTarea';
import ItemDocumento from '../../models/ItemDocumento';
import ItemExtension from '../../models/ItemExtension';
import moment from 'moment';

const panelStyles : IPanelStyles = {
    root: {},
    overlay: {},
    hiddenPanel: {},
    main: {},
    commands: {
        marginTop: 0,
    },
    contentInner: {},
    scrollableContent: {},
    navigation: {
        backgroundColor: 'var(--color-primario)',
        padding: 10,
        alignItems: 'center',
    },
    header: {
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 5,
        paddingBottom: 5,
    },
    headerText: {
        color: '#ffffff',
    },
    content: {
        padding: 0,
    },
    footer: {},
    footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: {
                color: '#ffffff',
                backgroundColor: 'transparent',
                padding: 0,
            },
            rootHovered: {
                backgroundColor: '#000000',
                color: '#ffffff',
                borderColor: '#000000',
            },
        },
    },
};

const formClasses = mergeStyleSets({
    footer: {
        padding: '12px',
        display: 'flex',
        justifyContent: 'flex-end',
        borderTop: '1px solid #ececec',
        marginTop: 8,
    },
    saveButton: {
        backgroundColor: 'var(--color-primario)',
        borderColor: 'var(--color-primario)',
    },
});

const getExtension = (fileName: string) : string => {
    let fileArr : string[] =  fileName.split('.');
    return fileArr[fileArr.length - 1];
};

const file2Base64 = (file: File) : Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () : void => {
            resolve(reader.result?.toString() || '');
        };
        reader.onerror = (error: ProgressEvent<FileReader>) : void => {
            reject(error);
        };
    });
};

interface IWorkDocumentCreateComponentProps {
    work?: ItemTarea;
    workOrderId?: number;
    onClose: () => void;
    onWorkDocumentCreated?: () => void;
};

const WorkDocumentCreateComponent = (props: IWorkDocumentCreateComponentProps) => {
    const auth = useAuthStatus();

    const [workDocument, setWorkDocument] = useState<ItemDocumento>(new ItemDocumento());
    const [extensions, setExtensions] = useState<ItemExtension[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        setWorkDocument((workDocument: ItemDocumento) => {
            workDocument.IdElemento = 1100;
            workDocument.fecDoc = moment().format('YYYY-MM-DD HH:mm:ss');
            return workDocument;
        });
    }, [workDocument]);

    useEffect(() => {
        if (props.workOrderId) {
            setWorkDocument((d: ItemDocumento) => {
                d.IdElemento = 1000;
                d.Clave = props.workOrderId!.toString();
                return d;
            });
        }
    }, [props.workOrderId]);

    const onFileChange = (event: any) : void => {
        setError('');
        let file : File = event.target.files[0];
        if (!extensions.some((e: ItemExtension) => e.TipoDato === file.type)) {
            event.target.value = null;
            setError("Extensión desconocida.");
        }
        else if (file.size > 2097152) {
            event.target.value = null;
            setError("El archivo no debe superar los 2 MB.");
        }
        else {
            setLoading(true);
            file2Base64(file).then((response: string) => {
                setWorkDocument({
                    ...workDocument,
                    NombreArchivo: file.name,
                    Extension: getExtension(file.name),
                    Size: file.size.toString(),
                    imgBase64: response,
                    Imagen: response,
                });
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    };

    const onChangeDescription = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setWorkDocument({
            ...workDocument,
            Descripcion: newValue ? newValue : "",
        });
    };
    
    const onChangeIsSignature = (ev?: React.FormEvent<HTMLInputElement | HTMLElement>, checked?: boolean) : void => {
        setWorkDocument({
            ...workDocument,
            EsFirma: checked || false,
        });
    };

    const onSubmit = () : void => {
        if (auth.user) {
            setLoading(true);
            setError('');
            ApiService.Post<string>("OrdenesTrabajo", "SetDocumentoAdjunto", auth.user, {
                "ItemDocumentoAdjunto": workDocument,
            }).then(() => {
                if (props.onWorkDocumentCreated) {
                    props.onWorkDocumentCreated();
                }
                props.onClose();
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    };

    useEffect(() => {
        if (props.work) {
            setWorkDocument((workDocument: ItemDocumento) => {
                workDocument.Clave = props.work!.Id.toString();
                return workDocument;
            });
        }
    }, [props.work]);

    useEffect(() => {
        if (auth.user) {
            setLoading(true);
            ApiService.Post<ItemExtension[]>("OrdenesTrabajo", "GetDocumentosExtensiones", auth.user).then((response: ItemExtension[]) => {
                setExtensions(response);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    }, [auth.user]);

    return (
        <Panel
            isOpen={true}
            headerText="Nuevo documento adjunto"
            onDismiss={props.onClose}
            styles={panelStyles}
            type={PanelType.medium}>
            <div className="container-fluid p-0">
                <div className="row m-0">
                    <div className='col-12 px-3 py-1'>
                        <div className='row m-0 align-items-center'>
                            <div className='col-3 p-0'>
                                Documento
                            </div>
                            <div className='col-9 p-0'>
                                <input 
                                    type="file" 
                                    onChange={onFileChange} />
                            </div>
                        </div>
                    </div>
                    <div className='col-12 px-3 py-1'>
                        <div className='row m-0 align-items-center'>
                            <div className='col-3 p-0'>
                                Descripción del documento
                            </div>
                            <div className='col-9 p-0'>
                                <TextField
                                    value={workDocument.Descripcion} 
                                    onChange={onChangeDescription} 
                                    rows={5}
                                    multiline />
                            </div>
                        </div>
                    </div>
                    <div className='col-12 px-3 py-1'>
                        <div className='row m-0 align-items-center'>
                            <div className='col-12 p-0'>
                                <Checkbox
                                    className='my-2'
                                    label='Es firma?'
                                    onChange={onChangeIsSignature} 
                                    checked={workDocument.EsFirma} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='row m-0'>
                    <div className='col-12'>
                        <ErrorMessageComponent message={error} />
                    </div>
                </div>
                <div className={formClasses.footer}>
                    <PrimaryButton
                        text="Guardar"
                        className={formClasses.saveButton}
                        onClick={onSubmit} />
                </div>
            </div>
            <LoaderComponent loading={loading} />
        </Panel>
    );
};

export default WorkDocumentCreateComponent;
